<template v-if="ready">
    <section class="pt-16 pb-12 bg-blue-800">
        <div class="container px-4 mx-auto">
            <div class="flex flex-wrap items-center -mx-4">
                <div class="w-full px-4 xl:mb-0">

                    <h2 class="font-heading text-3xl sm:text-4xl mb-6 text-blue-300 font-bold">
                        Find an Integrative Health Practitioner
                    </h2>
                    <div class="flex flex-wrap">
                        <p class="w-full xl:w-2/3 pr-2 text-lg leading-8 text-white">Search for integrative physicians and health experts. Partner with Andrew Weil Center-trained integrative providers who value your well-being, combine evidence-based methods to improve your health and merge conventional and natural approaches to address your mind, body, and spirit.</p>
                    </div>
                </div>
            </div>
            <div class="mt-6 mb-6 text-white">
                <!-- search fields -->
                <div class="flex flex-wrap -mx-3">
                    <div class="w-full md:w-1/4 px-3 mb-6">
                        <label :class="labelClass">Location (City, State, or Postal Code)</label>
                        <div class="w-full">
                            <input :class="inputClass" type="text" v-model="postalCode">
                        </div>
                    </div>

                    <div class="w-full md:w-1/4 px-3 mb-6 md:mb-0">
                        <label :class="labelClass">Within a radius of</label>
                        <div class="w-full">
                            <select :disabled="disableDistance"
                                    :class="[selectClass, disableDistance ? 'cursor-not-allowed' : 'cursor-pointer' ]"
                                    v-model="distance">
                                <option value="999999"></option>
                                <option value="10">10 miles</option>
                                <option value="20">20 miles</option>
                                <option value="50">50 miles</option>
                                <option value="100">100 miles</option>
                                <option value="250">250 miles</option>
                            </select>
                        </div>
                        <div class="w-3/4"></div>
                    </div>
                    <div class="w-full md:w-full px-3 mb-6 md:mb-0">
                        <p class="text-xl">
                            or
                        </p>
                    </div>
                    <div class="w-full md:w-full px-3 mb-6">
                        <label :class="labelClass">Country</label>
                        <div class="w-1/4">
                            <select :class="selectClass" v-model="selectedCountry">
                                <option value="All"></option>
                                <option v-for="country in countries" :value="country.Iso2">
                                    {{ country.name }}
                                </option>
                            </select>
                        </div>
                        <div class="w-3/4"></div>
                    </div>

                    <!-- filters -->
                    <div class="w-full md:w-1/4 px-3 mb-6 md:mb-0">
                        <label for="specialitySelect" :class="labelClass">Specialty</label>
                        <select id="specialitySelect" :class="selectClass" v-model="selectedSpeciality">
                            <option value="All"></option>
                            <option v-for="speciality in specialities" :value="speciality">
                                {{ speciality }}
                            </option>
                        </select>
                    </div>
                    <div class="w-full md:w-1/4 px-3 mb-6 md:mb-0">
                        <label for="professionSelect" :class="labelClass">Profession</label>
                        <select id="professionSelect" :class="selectClass" v-model="selectedProfession">
                            <option value="All"></option>
                            <option v-for="profession in professions" :value="profession">
                                {{ profession }}
                            </option>
                        </select>
                    </div>
                    <div class="w-full md:w-1/4 px-3">
                        <label class="block">
                            <input v-model="telemedicine" class="mr-2 leading-tight" type="checkbox">
                            Provider Offers Telemedicine Appointments
                        </label>
                    </div>
                </div>
                <input
                    :class="[ disableSubmit ? 'cursor-not-allowed' : 'cursor-pointer' ]"
                    class="btn-green mt-6"
                    :disabled="disableSubmit"
                    @click="doSearch"
                    type="submit"
                    :value="[ asyncCallInProgress ? 'Loading...' : 'Search' ]"
                >
                <input
                    class="btn-green mt-6"
                    @click="resetAll"
                    type="submit"
                    value="Reset"
                >
                <i v-show="asyncCallInProgress" class="fa-duotone fa-spinner fa-spin-pulse"></i>
                <transition name="fade" mode="out-in">
                    <p class="resultsMsg" v-if="showNumResults && !initSearch">{{ filteredClinics.length }} result(s).</p>
                    <p class="resultsMsg" v-else><br></p>
                </transition>
            </div>
        </div>
    </section>
    <section v-if="error" class="py-16">
        {{ error }}
    </section>
    <section v-if="initSearch" class="py-16 standard-content">
        <div class="container px-4 mx-auto">
            <h3>Start your search by entering a postal code or a country and any of the other optional selections above.</h3>
        </div>
    </section>
    <section v-else-if="filteredClinics.length === 0" class="py-16 standard-content">
        <div class="container px-4 mx-auto">
            <h3>No results found.</h3>
        </div>
    </section>
    <section v-if="showClinics" class="py-16">
        <div class="container px-4 mx-auto">
            <div class="flex flex-wrap -m-3 mb-16">
                <template v-for="clinic in filteredClinics">
                    <div class="w-full md:w-1/2 lg:w-1/3 p-3 alumCard">
                        <div class="p-6 border">
                            <h3 class="mb-4 text-2xl font-semibold font-heading text-blue-800">
                                <i class="fas fa-user-circle" aria-hidden="true"></i>&nbsp;{{ clinic.clinicianName }}, {{ clinic.credentials }}
                            </h3>
                            <template v-if="clinic.name">
                                <h4>{{ clinic.name }}</h4>
                            </template>
                            <p v-if="clinic.speciality" class="mb-4 leading-relaxed">
                                {{ clinic.speciality }}
                            </p>
                            <p class="mb-4 leading-relaxed">
                                {{ clinic.address }}<br/>
                                <span v-show="showCityState(clinic.cityStateZip)">{{ clinic.cityStateZip }}</span><br/>
                                {{ clinic.distanceStr }}<br/>
                                <template v-if="clinic.telemedicine">
                                    <strong>Telemedicine Visits Available</strong>
                                    <img src="/file/73446/telemedicine.svg" class="inline) h-10 ml-2" alt=""/>
                                </template>
                            </p>
                            <a class="btn-red cursor-pointer" @click="learnMore(clinic.clinicId)">Learn more</a>
                        </div>
                    </div>
                </template>
            </div>

            <ClinicModal
                v-if="currentClinic && showModal"
                :current-clinic="currentClinic" :show-modal="showModal"
                @close-modal="this.showModal = false"
                @keyup.esc="this.showModal = false"/>

        </div>
    </section>
    <section v-else class="pg-16">
    </section>
</template>

<style scoped>
.resultsMsg {
    padding-top: 8px;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .5s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

</style>

<script>
import ClinicModal from './ClinicModal.vue'
import axios from "axios"

export default {
    components: {
        ClinicModal
    },
    data() {
        return {
            servletEndpoint: "/googleMapsPlatform",
            ready: false,
            error: null,
            showClinics: false,
            showNumResults: false,
            currentClinic: null,
            showModal: false,
            asyncCallInProgress: false,
            inputClass: "w-full py-3 pl-4 leading-tight text-gray-700 bg-gray-200 focus:bg-white border border-gray-200 focus:border-gray-500",
            selectClass: "appearance-none block w-full py-3 px-4 leading-tight text-gray-700 bg-gray-200 focus:bg-white border border-gray-200 focus:border-gray-500 focus:outline-none",
            labelClass: "block mb-2 text-xs uppercase tracking-wide font-bold",
            postalCode: null,
            countries: [],
            specialities: [],
            professions: [],
            clinics: [],
            filteredClinics: [],
            selectedCountry: 'All',
            selectedSpeciality: 'All',
            selectedProfession: 'All',
            distance: null,
            telemedicine: false,
            timer: null,
            initSearch: true,
            lastTouched: null,
            fieldSelects: null
        }
    },
    watch: {
        postalCode: function () {
            this.lastTouched = 'postalCode'
        },
        selectedCountry: function () {
            this.lastTouched = 'country'
        },
        distance: function () {
            this.doFilters()
        },
        selectedSpeciality: function () {
            if (this.fieldSelects === 0b0001) {
                this.initSearch = true
                this.filteredClinics = []
            } else {
                this.doFilters()
            }
        },
        selectedProfession: function () {
            if (this.fieldSelects === 0b0010) {
                this.initSearch = true
                this.filteredClinics = []
            } else {
                this.doFilters()
            }
        },
        telemedicine: function () {
            this.doFilters()
        },
        filteredClinics: function () {
            clearTimeout(this.timer)
            this.showNumResults = true
            this.timer = setTimeout(() => {
                this.showNumResults = false
            }, 3000)
        }
    },
    computed: {
        disableSubmit() {
            if (this.asyncCallInProgress)
                return true

            if (this.selectedProfession === 'All' &&
                this.selectedCountry === 'All' &&
                this.selectedSpeciality === 'All' &&
                (this.postalCode === null || this.postalCode === '')) {
                return true
            }

            return false
        },
        disableDistance() {
            return !!(this.selectedCountry && !this.postalCode);
        }
    },
    methods: {
        showCityState(cityStateZip) {
            return !(typeof cityStateZip === 'undefined' || cityStateZip === '');
        },
        setFieldSelects() {
            let fieldSelects = 0b0000
            fieldSelects += this.selectedProfession === 'All' ? 0b0000 : 0b0010
            fieldSelects += this.selectedSpeciality === 'All' ? 0b0000 : 0b0001
            this.fieldSelects = fieldSelects
        },
        doFilters() {
            if (this.clinics === null || this.clinics.length === 0) {
                this.filteredClinics = this.clinics
            } else {
                this.filteredClinics = this.clinics.filter(this.filterFunction)
            }
        },
        filterFunction: function (clinic) {
            let specialtyMatch = this.selectedSpeciality === null || this.selectedSpeciality === 'All' || clinic.speciality === this.selectedSpeciality
            let professionMatch = this.selectedProfession === null || this.selectedProfession === 'All' || clinic.profession === this.selectedProfession
            let telemedicineMatch = !this.telemedicine || (this.telemedicine && clinic.telemedicine)
            let distanceMatch = this.distance === null || clinic.distanceInMiles <= this.distance
            return professionMatch && specialtyMatch && telemedicineMatch && distanceMatch
        },
        learnMore: function (clinic_id) {
            this.currentClinic = this.clinics.find(({clinicId}) => clinicId === clinic_id);
            this.showModal = true;
        },
        resetAll: function () {
            this.postalCode = null
            this.clinics = []
            this.filteredClinics = []
            this.selectedCountry = 'All'
            this.selectedSpeciality = 'All'
            this.selectedProfession = 'All'
            this.distance = null
            this.telemedicine = false
            this.timer = null
            this.initSearch = true
            this.lastTouched = null
            this.fieldSelects = null
        },
        doSearch: async function () {
            let endpoint = new URL(window.location.origin + this.servletEndpoint)
            if (this.lastTouched === 'postalCode') {
                endpoint.searchParams.append("postalCode", this.postalCode)
                this.selectedCountry = 'All'
            } else if (this.lastTouched === 'country') {
                endpoint.searchParams.append("country", this.selectedCountry)
                this.postalCode = ''
                this.distance = null
            }

            if (this.selectedSpeciality)
                endpoint.searchParams.append("speciality", this.selectedSpeciality)

            if (this.selectedProfession)
                endpoint.searchParams.append("profession", this.selectedProfession)

            if (this.telemedicine)
                endpoint.searchParams.append("telemedicine", "")

            this.asyncCallInProgress = true
            await axios
                .get(endpoint)
                .then((response) => {
                    this.error = response.data.error
                    if (typeof this.error === 'undefined') {
                        this.clinics = this.resolveClinics(response.data.clinics)
                        this.setFieldSelects()
                        this.doFilters();
                        this.showClinics = true
                        this.initSearch = false
                    }
                })
                .catch((error) => {
                    console.log(error)
                });

            this.asyncCallInProgress = false
        },
        resolveClinics(clinics) {
            for (let i = 0; i < clinics.length; i++) {
                let clinic = clinics[i]
                if (clinic.distanceInMiles !== null) {
                    if (clinic.distanceInMiles < 1)
                        clinic.distanceStr = "Less than a mile away"
                    else if (clinic.distanceInMiles === 1)
                        clinic.distanceStr = clinic.distanceInMiles + " mile away"
                    else
                        clinic.distanceStr = clinic.distanceInMiles + " miles away"
                }
            }

            return clinics
        },
        doPreFetch: async function () {
            this.asyncCallInProgress = true
            await axios
                .get(this.servletEndpoint + "/preFetch")
                .then((response) => {
                    this.specialities = response.data.specialities
                    this.professions = response.data.professions
                    this.getCountries(response.data.countries)
                    this.ready = true
                })
                .catch((error) => {
                    console.log(error)
                });

            this.asyncCallInProgress = false
        },
        getCountries: async function (countries) {
            this.asyncCallInProgress = true
            await axios
                .get('https://countriesnow.space/api/v0.1/countries/iso')
                .then((response) => {
                    let apiCountries = response.data.data
                    for (let i = 0; i < countries.length; i++) {
                        let country = countries[i]
                        let j = apiCountries.findIndex(x => x.Iso2 === country)
                        if (j > -1)
                            this.countries.push(apiCountries[j])
                    }
                })
                .catch((error) => {
                    console.log(error)
                });
        }
    },
    mounted() {
        this.doPreFetch()
    },
};
</script>